import _ from "lodash";
import {
  cancelTransfer,
  returnCoreToCustomer,
  update,
  unreserveForRepair,
  unreserveForSale,
  cancelModTo,
  deleteInventory,
  reclaim
} from "MODULES/inventories/actions";
import {
  QUALITY_CONTROL,
  REPAIR,
  WAREHOUSE,
  SALES,
  CAN_RELEASE_QC_HOLD,
  CREATE_VENDOR_RMAS,
  ACCOUNTING_MANAGER,
  SALES_MANAGER,
  WAREHOUSE_MANAGER,
  WAREHOUSE_LOGISTICS_DIRECTOR,
  QUALITY_CONTROL_MANAGER,
  ACCOUNTING,
  ADMIN
} from "MODULES/users/constants";
import {
  AS_REMOVED,
  CORE_RETURN_RECEIVED,
  EXCHANGE_GONE_OUTRIGHT,
  IN_STOCK,
  INTERNAL_OUTRIGHT,
  RESERVE_FOR_SALE,
  RESERVE_FOR_REPAIR,
  RESERVE_FOR_SHOP_ASSIST,
  REPAIRED,
  ON_ORDER_FOR_SALE,
  AWAITING_SCRAP_REPORT_AT_SHOP,
  AWAITING_SCRAP_REPORT_LOCAL,
  CREATE_COMMERCIAL_INVOICE,
  CREATE_DUMMY_RECORD,
  REPAIR_AND_HOLD,
  RESERVE_INV_FOR_SALE,
  RESERVE_INV_FOR_REPAIR,
  RETURN_CORE_TO_CUSTOMER,
  RETURN_TO_STOCK,
  RMA_RECEIVED,
  UNRESERVE_REPAIR,
  UPDATE_SRP,
  UPDATE_QC_STATUS,
  TRANSFER_INVENTORY,
  VENDOR_RMA_REQUESTED,
  CANCEL_TRANSFER,
  QC_HOLD,
  SCRAP,
  SCRAP_REPORT_RECEIVED,
  SCRAP_INV_LOCALLY,
  SCRAP_AT_SHOP,
  SPLIT_INVENTORY,
  CREATE_VENDOR_RMA,
  CUSTOMER_RMA_REQUESTED,
  DISCREPANCY_REPORTED,
  GONE,
  REMOVE_EMA,
  DO_NOT_SELL,
  CANCEL_MOD_TO,
  RETURN_TO_CUSTOMER,
  RETURNING_TO_CUSTOMER,
  APPLY_LOT_COST,
  CANCEL_CONSIGNMENT,
  ASSIGN_EXCHANGE,
  CORE_RETURNED_TO_CUSTOMER,
  PRINT,
  RETURN_TO_CUSTOMER_V2,
  REQUEST_SCRAP_AT_SHOP,
  DOWNLOAD_SCRAP_CERT
} from "MODULES/inventories/constants";
import {
  BEYOND_ECON_REPAIR,
  CONSIGNMENT,
  EMA
} from "MODULES/Receiving/constants";
import {
  hasAnyOfRoles,
  hasAnyOfSubroles,
  hasPermission,
  hasRole
} from "MODULES/users/helpers";
import { isManagedRepair } from "MODULES/inventories/helpers";
import { finalWarrantyApproval } from "../../../../../RMAsAndWarranties/components/Detail/Panel/WarrantyConsideration/WarrantyConsideration";

const lockedStatuses = [
  GONE,
  RESERVE_FOR_SALE,
  ON_ORDER_FOR_SALE,
  RETURNING_TO_CUSTOMER
];

const rules = {
  inventoryUnlocked: inv => lockedStatuses.indexOf(inv.status) === -1,
  noActiveRepairOrder: inv =>
    !inv.repair_status ||
    [REPAIRED, REPAIR_AND_HOLD].includes(inv.repair_status),
  noActiveVendorRma: inv => inv.status !== VENDOR_RMA_REQUESTED,
  sellable: inv =>
    inv.sellable_status !== DO_NOT_SELL ||
    ([EXCHANGE_GONE_OUTRIGHT, INTERNAL_OUTRIGHT].includes(inv.repair_status) &&
      inv.condition === AS_REMOVED &&
      inv.status !== RESERVE_FOR_SALE),
  notManagedRepair: inv => !isManagedRepair(inv),
  canUnreserveSale: inv =>
    inv.status === RESERVE_FOR_SALE && inv.can_unreserve_sales,
  canUnreserveRepair: inv =>
    [RESERVE_FOR_REPAIR, RESERVE_FOR_SHOP_ASSIST].includes(inv.repair_status) &&
    inv.can_unreserve_repair &&
    !inv.managed_repair_in_progress &&
    inv.status !== RMA_RECEIVED,
  awaitingScrap: inv =>
    [AWAITING_SCRAP_REPORT_AT_SHOP, AWAITING_SCRAP_REPORT_LOCAL].includes(
      inv.status
    ),
  canRemoveEma: inv => inv.consignment_type === EMA,
  notGone: inv => inv.status !== GONE,
  canReturnCore: inv =>
    (inv.status === CORE_RETURN_RECEIVED && inv.qc_status === QC_HOLD) ||
    (inv.exchange_eval_in_progress && inv.status === DISCREPANCY_REPORTED),
  canReturnToCustomerV1: inv =>
    inv.condition === BEYOND_ECON_REPAIR &&
    inv.status === IN_STOCK &&
    Number(inv.cost_per_unit) === 0 &&
    inv.sales_order_line_items &&
    inv.sales_order_line_items.some(s => !s.cancelled && s.exchange_sale),
  canScrapLocally: inv => inv.can_scrap_local,
  canScrapAtShop: inv => inv.can_scrap_at_shop,
  canSplit: inv => inv.quantity > 1,
  canTransfer: (inv, currentUser) =>
    inv.transferrable &&
    currentUser.locations.length > 1 &&
    !inv.managed_repair_repaired &&
    !inv.managed_repair_in_progress,
  dummyDoesNotExist: inv => !inv.dummy,
  canReturnCoreToStock: inv => inv.status === CORE_RETURN_RECEIVED,
  canReturnRmaRequestToStock: inv =>
    inv.status === RMA_RECEIVED &&
    (!_.get(inv, "most_recent_customer_rma.warranty_consideration") ||
      (inv.most_recent_customer_rma &&
        finalWarrantyApproval(inv.most_recent_customer_rma))),
  canRemoveModTo: inv =>
    inv.mod_to_id && [null, "repaired"].includes(inv.repair_status),
  canCancelTransfer: inv =>
    inv.active_transfer && inv.active_transfer.cancellable,
  canDownload: inv => inv.active_transfer,
  undoScrap: inv =>
    [AWAITING_SCRAP_REPORT_AT_SHOP, AWAITING_SCRAP_REPORT_LOCAL].includes(
      inv.status
    )
};

export default {
  applyLotCost: {
    dropDownText: "Apply Lot Cost",
    rules: [
      inv =>
        Number(inv.cost_per_unit) === 0 &&
        ["LotLineItem", "Lot"].includes(inv.receiving_line_item_type)
    ],
    iconName: "dollar",
    key: APPLY_LOT_COST
  },
  assignExchange: {
    dropDownText: "Assign as Core Return",
    rules: [
      inv => inv.requires_assigned_core_return && inv.status === "in_stock"
    ],
    iconName: "undo",
    key: ASSIGN_EXCHANGE,
    authorized: user => hasAnyOfRoles(user, [QUALITY_CONTROL])
  },
  cancelModTo: {
    text: "Are you sure you want to cancel this Mod To?",
    confirmText: "Yes",
    cancelText: "No",
    rules: [rules.canRemoveModTo],
    authorized: user => hasAnyOfRoles(user, [QUALITY_CONTROL, WAREHOUSE]),
    key: CANCEL_MOD_TO,
    dropDownText: "Cancel Mod To",
    iconName: "ban",
    btnClass: "btn-danger",
    action: inv =>
      cancelModTo(inv.id, {
        suggested_retail: _.get(
          inv,
          "purchase_order_line_items[0].suggested_retail"
        )
      })
  },
  cancelTransfer: {
    text: "Are you sure you want to cancel this transfer?",
    confirmText: "Yes",
    cancelText: "No",
    rules: [rules.canCancelTransfer],
    authorized: user => hasAnyOfRoles(user, [WAREHOUSE, QUALITY_CONTROL]),
    key: CANCEL_TRANSFER,
    dropDownText: "Cancel Transfer",
    iconName: "ban",
    btnClass: "btn-danger",
    action: inv => cancelTransfer(inv.id, inv.active_transfer.id)
  },
  createDummy: {
    iconName: "copy",
    rules: [rules.dummyDoesNotExist, rules.notGone],
    dropDownText: "Create Dummy",
    authorized: (user, inv) => hasRole(user, SALES) && !isManagedRepair(inv),
    key: CREATE_DUMMY_RECORD
  },
  deleteInventory: {
    dropDownText: "Delete Inventory",
    rules: [
      (inv, user) =>
        (inv.imported && inv.status !== GONE) ||
        (Number(inv.cost_per_unit) === 0 && user.role === ADMIN)
    ],
    iconName: "trash",
    action: deleteInventory,
    text: "Are you sure you want to permanently delete this inventory?",
    authorized: (user, inv) =>
      hasAnyOfSubroles(user, [
        ACCOUNTING_MANAGER,
        QUALITY_CONTROL_MANAGER,
        SALES_MANAGER,
        WAREHOUSE_MANAGER,
        WAREHOUSE_LOGISTICS_DIRECTOR
      ]) || inv.buyer_id === user.id
  },
  createCommercialInvoice: {
    iconName: "file",
    rules: [rules.canDownload],
    dropDownText: "Download Commercial Invoice",
    key: CREATE_COMMERCIAL_INVOICE
  },
  print: {
    rules: [],
    key: PRINT,
    dropDownText: "Print Inventory Label",
    iconName: "gears"
  },
  unreserveForSale: {
    text: "Are you sure you want to return this item to stock?",
    iconName: "plus-circle",
    rules: [rules.canUnreserveSale],
    dropDownText: "Return To Stock",
    action: unreserveForSale, // over here!
    authorized: user =>
      hasAnyOfRoles(user, [ACCOUNTING, QUALITY_CONTROL, SALES]),
    confirmText: "Yes, Return To Stock",
    key: RETURN_TO_STOCK
  },
  reclaimInventory: {
    text: "You're reclaiming abandoned inventory, are you sure?",
    iconName: "superpowers",
    rules: [
      inv =>
        (inv.status === GONE || inv.repair_status === EXCHANGE_GONE_OUTRIGHT) &&
        inv.repair_status !== CORE_RETURNED_TO_CUSTOMER &&
        inv.exchange_gone_outright &&
        !inv.reclaimed
    ],
    action: reclaim,
    dropDownText: "Reclaim",
    confirmText: "Yes, Reclaim Inventory"
  },
  returnCoreToCustomer: {
    text: "Are you sure you want to return the core to the customer?",
    iconName: "warning",
    rules: [rules.canReturnCore],
    dropDownText: "Reject Core Return - Return Core To Customer",
    action: returnCoreToCustomer,
    key: RETURN_CORE_TO_CUSTOMER
  },
  updateEma: {
    text: "Are you sure you want to remove this EMA?",
    iconName: "trash",
    rules: [rules.canRemoveEma],
    dropDownText: "Remove EMA",
    action: () => update({ status: GONE }),
    key: REMOVE_EMA
  },
  reserveForRepair: {
    iconName: "wrench",
    rules: [
      rules.inventoryUnlocked,
      rules.noActiveRepairOrder,
      rules.noActiveVendorRma,
      inv => !inv.managed_repair_repaired
    ],
    dropDownText: "Reserve For Repair",
    authorized: user => hasAnyOfRoles(user, [QUALITY_CONTROL, REPAIR]),
    key: RESERVE_INV_FOR_REPAIR
  },
  reserveForSale: {
    text: "Are you sure you want to reserve for sale",
    iconName: "dollar",
    rules: [rules.inventoryUnlocked, rules.notManagedRepair, rules.sellable],
    dropDownText: "Reserve For Sale",
    authorized: (user, inv) =>
      hasRole(user, SALES) || inv.reserve_for_seller_id === user.id,
    key: RESERVE_INV_FOR_SALE
  },
  cancelConsignment: {
    dropDownText: "Return to Consignor",
    rules: [
      inv => inv.consignment_type === CONSIGNMENT && inv.status === IN_STOCK
    ],
    iconName: "ban",
    key: CANCEL_CONSIGNMENT,
    authorized: user => hasAnyOfRoles(user, QUALITY_CONTROL)
  },
  returnToCustomer: {
    dropDownText: "Return To Customer",
    text: "Are you sure you want to return this item to the customer?",
    iconName: "undo",
    rules: [rules.canReturnToCustomerV1],
    key: RETURN_TO_CUSTOMER,
    confirmText: "Yes, Return To Customer"
  },
  returnToCustomerV2: {
    dropDownText: "Return To Customer",
    text: "Are you sure you want to return this item to the customer?",
    iconName: "undo",
    rules: [
      inv =>
        inv.eligible_to_return_to_customer &&
        !rules.canReturnToCustomerV1(inv) &&
        inv.most_recent_customer_rma &&
        inv.status === IN_STOCK
    ],
    key: RETURN_TO_CUSTOMER_V2,
    confirmText: "Yes, Return To Customer"
  },
  returnToStock: {
    text: "Are you sure you want to return this item to stock?",
    iconName: "plus-circle",
    rules: [
      inv =>
        rules.canReturnCoreToStock(inv) || rules.canReturnRmaRequestToStock(inv)
    ],
    dropDownText: "Return To Stock",
    action: () => update({ status: IN_STOCK }),
    authorized: (user, inv) =>
      hasAnyOfRoles(user, [QUALITY_CONTROL, ACCOUNTING]) ||
      hasAnyOfSubroles(user, [SALES_MANAGER]) ||
      [inv.reserved_by_id, inv.reserve_for_seller_id].includes(user.id),
    confirmText: "Yes, Return To Stock",
    key: RETURN_TO_STOCK
  },
  requestScrapAtShop: {
    text: "Are you sure you want to scrap this item?",
    iconName: "trash",
    rules: [rules.canScrapAtShop],
    authorized: user => hasAnyOfRoles(user, [QUALITY_CONTROL, REPAIR]),
    dropDownText: "Request Scrap At Shop",
    key: REQUEST_SCRAP_AT_SHOP
  },
  scrapAtShop: {
    text: "Are you sure you want to scrap this item?",
    iconName: "trash",
    rules: [rules.canScrapAtShop],
    authorized: user => hasAnyOfRoles(user, [QUALITY_CONTROL, REPAIR]),
    action: () => update({ status: AWAITING_SCRAP_REPORT_AT_SHOP }),
    dropDownText: "Scrap At Shop",
    confirmText: "Yes, Scrap Item",
    key: SCRAP_AT_SHOP
  },
  scrapLocally: {
    text: "Are you sure you want to scrap this item?",
    iconName: "trash",
    rules: [rules.canScrapLocally],
    authorized: user => hasAnyOfRoles(user, [QUALITY_CONTROL, WAREHOUSE]),
    dropDownText: "Scrap Locally",
    action: () => update({ status: AWAITING_SCRAP_REPORT_LOCAL }),
    key: SCRAP_INV_LOCALLY
  },
  scrapReportDownload: {
    authorized: user => hasAnyOfRoles(user, [QUALITY_CONTROL, WAREHOUSE]),
    iconName: "cloud-download",
    dropDownText: "Download Scrap Cert",
    key: DOWNLOAD_SCRAP_CERT,
    rules: [inv => inv.condition === SCRAP]
  },
  scrapReportReceived: {
    iconName: "cloud-download",
    rules: [rules.inventoryUnlocked, rules.awaitingScrap],
    dropDownText: "Scrap Report Received",
    key: SCRAP_REPORT_RECEIVED
  },
  splitInventory: {
    iconName: "code-fork",
    rules: [rules.canSplit],
    dropDownText: "Split Inventory",
    key: SPLIT_INVENTORY
  },
  transfer: {
    rules: [rules.canTransfer],
    authorized: user => hasAnyOfRoles(user, [WAREHOUSE, QUALITY_CONTROL]),
    key: TRANSFER_INVENTORY,
    dropDownText: "Transfer Inventory",
    iconName: "plane"
  },
  undoScrap: {
    text: "Are you sure you want to undo scrap?",
    iconName: "dollar",
    rules: [rules.undoScrap],
    dropDownText: "Undo Scrap",
    authorized: user => hasAnyOfRoles(user, [QUALITY_CONTROL, REPAIR]),
    key: SCRAP
  },
  unreserveForRepair: {
    text: "Are you sure you want to return this item to stock?",
    iconName: "plus-circle",
    rules: [rules.canUnreserveRepair],
    dropDownText: "Unreserve For Repair",
    authorized: user => hasAnyOfRoles(user, [QUALITY_CONTROL, REPAIR]),
    action: unreserveForRepair,
    confirmText: "Yes, Return To Stock",
    key: UNRESERVE_REPAIR
  },
  updateQCStatus: {
    iconName: "pause-circle",
    rules: [],
    dropDownText: "Update QC Status",
    authorized: (user, inv) =>
      (hasPermission(user, CAN_RELEASE_QC_HOLD) ||
        hasRole(user, QUALITY_CONTROL) ||
        (inv.receiving_line_item_type === "RepairOrderLineItem" &&
          hasRole(user, REPAIR))) &&
      !inv.managed_repair_in_progress,
    key: UPDATE_QC_STATUS
  },
  updateSrp: {
    iconName: "dollar",
    rules: [inv => inv.status !== GONE],
    dropDownText: "Update SRP",
    authorized: (user, inv) => !isManagedRepair(inv),
    key: UPDATE_SRP
  },
  vendorRmaRequest: {
    iconName: "shield",
    rules: [
      rules.notGone,
      inv =>
        ![
          CUSTOMER_RMA_REQUESTED,
          VENDOR_RMA_REQUESTED,
          RMA_RECEIVED,
          RETURNING_TO_CUSTOMER
        ].includes(inv.status) &&
        !inv.managed_repair_repaired &&
        !inv.managed_repair_in_progress
    ],
    authorized: user =>
      hasRole(user, QUALITY_CONTROL) || hasPermission(user, CREATE_VENDOR_RMAS),
    key: CREATE_VENDOR_RMA,
    dropDownText: "RMA/Partial Credit Request"
  }
};



// WEBPACK FOOTER //
// ./app/modules/inventories/components/Detail/Header/DropdownActions/config.js